import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, message, Modal, Select, Skeleton, Space, Typography } from 'antd';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import api from '../services/api';
import { TrainingDocument, TrainingFolder } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import { formatTrainingDate } from '../utils/trainingDates';
import { relatedTrainingMaterials } from '../utils/trainingDocuments';
import AdminTranscriptModal from './AdminTranscriptModal';
import AdminVideoSubtitlesModal from './AdminVideoSubtitlesModal';
import TrainingPdfViewer from './TrainingPdfViewer';
import VideoPlaybackPane from './VideoPlaybackPane';
import { normalizeTranscriptHtml } from './TranscriptEditor';
import './TranscriptEditor.css';
import './AdminTrainingPreview.css';

export interface AdminMaterialSelection { folderId: number; kind: 'video' | 'document'; id: number }
type MaterialKind = 'video' | 'presentation' | 'transcript';

const TranscriptPreview: React.FC<{ document: TrainingDocument; refresh: number }> = ({ document, refresh }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setHtml(null); setError('');
    api.get<{ html: string }>(`/video/admin/documents/${document.id}/text`, { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setHtml(normalizeTranscriptHtml(data.html)); })
      .catch(err => { if (!controller.signal.aborted) setError(getApiErrorMessage(err, 'Не удалось открыть текст')); });
    return () => controller.abort();
  }, [document.id, document.file_revision, refresh]);
  return error ? <Alert type="error" showIcon message={error} /> : html === null ? <Skeleton active />
    : <div className="training-transcript-html admin-training-preview-text" dangerouslySetInnerHTML={{ __html: html }} />;
};

const DownloadDocument: React.FC<{ document: TrainingDocument }> = ({ document }) => {
  const [busy, setBusy] = useState(false);
  return <Button icon={<DownloadOutlined />} loading={busy} onClick={async () => {
    setBusy(true);
    let url = '';
    try {
      const { data } = await api.get(`/video/admin/documents/${document.id}/file`, { responseType: 'blob' });
      url = URL.createObjectURL(data);
      const anchor = window.document.createElement('a');
      anchor.href = url; anchor.download = document.original_filename;
      window.document.body.appendChild(anchor); anchor.click(); anchor.remove();
    } catch (err) { message.error(getApiErrorMessage(err, 'Не удалось скачать файл')); }
    finally { if (url) setTimeout(() => URL.revokeObjectURL(url), 1000); setBusy(false); }
  }}>Скачать {document.document_kind === 'presentation' ? 'PDF' : 'TXT'}</Button>;
};

/** Admin reads are separate from learning progress and include hidden materials. */
const AdminTrainingPreview: React.FC<{
  folder: TrainingFolder | undefined; selection: AdminMaterialSelection;
  onClose: () => void; onRefresh: () => Promise<void>;
}> = ({ folder, selection, onClose, onRefresh }) => {
  const primary = selection.kind === 'video' ? folder?.videos.find(item => item.id === selection.id)
    : folder?.documents?.find(item => item.id === selection.id);
  const primaryKind: MaterialKind = selection.kind === 'video' ? 'video' : (primary as TrainingDocument | undefined)?.document_kind || 'presentation';
  const [choices, setChoices] = useState<Partial<Record<MaterialKind, number>>>(() => ({ [primaryKind]: selection.id }));
  const [editingText, setEditingText] = useState<TrainingDocument | null>(null);
  const [textRefresh, setTextRefresh] = useState(0);
  const [subtitlesOpen, setSubtitlesOpen] = useState(false);
  const [subtitlesRefresh, setSubtitlesRefresh] = useState(0);
  const materials = primary && folder ? relatedTrainingMaterials(primary, selection.kind, folder.videos, folder.documents || []) : { videos: [], documents: [] };
  const candidates = {
    video: materials.videos,
    presentation: materials.documents.filter(item => item.document_kind === 'presentation'),
    transcript: materials.documents.filter(item => item.document_kind === 'transcript'),
  };
  const selected = <T extends { id: number }>(items: T[], kind: MaterialKind): T | undefined => choices[kind] === undefined
    ? items.length === 1 ? items[0] : undefined : items.find(item => item.id === choices[kind]);
  const video = selected(candidates.video, 'video');
  const presentation = selected(candidates.presentation, 'presentation');
  const transcript = selected(candidates.transcript, 'transcript');
  const selector = (kind: MaterialKind, value: number | undefined, label: string) => candidates[kind].length > 1 && <Select
    aria-label={label} placeholder={label} value={value} className="admin-training-preview-select"
    options={candidates[kind].map(item => ({ value: item.id, label: `${item.title} · ${item.original_filename}` }))}
    onChange={(id: number) => setChoices(current => ({ ...current, [kind]: id }))} />;
  const materialCount = [candidates.video, candidates.presentation, candidates.transcript].filter(items => items.length).length;
  return <>
    <Modal className="video-admin-modal admin-training-preview" title={primary?.training_date
      ? `Обучение ${formatTrainingDate(primary.training_date)}` : primary?.title || 'Просмотр материала'}
      width={1600} open footer={null} maskClosable={false} onCancel={() => !editingText && !subtitlesOpen && onClose()}>
      <Typography.Paragraph type="secondary">{folder?.title} · Предпросмотр без изменения статистики сотрудников</Typography.Paragraph>
      {!primary ? <Alert type="warning" showIcon message="Материал перемещён или удалён. Закройте просмотр и откройте его из обновлённого списка." /> :
        <div className={`admin-training-preview-grid admin-training-preview-columns-${materialCount}`}>
          {candidates.video.length > 0 && <Card title="Видео" extra={video && <Button onClick={() => setSubtitlesOpen(true)}>Создать субтитры</Button>} className="admin-training-preview-panel admin-training-preview-video">
            {selector('video', video?.id, 'Выбор видео для предпросмотра')}
            {video ? <VideoPlaybackPane key={`${video.id}:${video.file_revision}`} video={video} adminPreview subtitlesRefresh={subtitlesRefresh} />
              : <Typography.Paragraph>Выберите видеозапись этого обучения.</Typography.Paragraph>}
          </Card>}
          {candidates.presentation.length > 0 && <Card title="Презентация" className="admin-training-preview-panel">
            {selector('presentation', presentation?.id, 'Выбор презентации для предпросмотра')}
            {presentation ? <>
              <Space wrap className="admin-training-preview-actions"><DownloadDocument document={presentation} /></Space>
              <TrainingPdfViewer key={`${presentation.id}:${presentation.file_revision}`} documentId={presentation.id} title={presentation.title} adminPreview />
            </> : <Typography.Paragraph>Выберите презентацию этого обучения.</Typography.Paragraph>}
          </Card>}
          {candidates.transcript.length > 0 && <Card title="Текст к презентации" className="admin-training-preview-panel">
            {selector('transcript', transcript?.id, 'Выбор текста для предпросмотра')}
            {transcript ? <>
              <Space wrap className="admin-training-preview-actions">
                <DownloadDocument document={transcript} />
                <Button icon={<EditOutlined />} onClick={() => setEditingText(transcript)}>Редактировать текст</Button>
              </Space>
              <TranscriptPreview document={transcript} refresh={textRefresh} />
            </> : <Typography.Paragraph>Выберите текст этого обучения.</Typography.Paragraph>}
          </Card>}
        </div>}
    </Modal>
    {subtitlesOpen && video && <AdminVideoSubtitlesModal key={`${video.id}:${video.file_revision}`} video={video} onClose={() => setSubtitlesOpen(false)} onComplete={() => setSubtitlesRefresh(value => value + 1)} />}
    {editingText && <AdminTranscriptModal document={editingText} onClose={() => setEditingText(null)} onSaved={async () => {
      await onRefresh(); setTextRefresh(value => value + 1);
    }} />}
  </>;
};

export default AdminTrainingPreview;
